"use client";

import clsx from "clsx";
import type { Session } from "next-auth";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { type FormEventHandler, MouseEventHandler, useId, useMemo, useState } from "react";

import {
	AddonInputType,
	type Addon,
	type Package,
	type Service,
	type BasketAddonItem,
} from "@/types";
import { calculatePackageTotal } from "@/lib/logic";
import { Currency, Discount } from "@/lib/constant";
import { addToBasket } from "@/features/basket/basketSlice";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { RadioGroup2, RadioGroupItem2 } from "@/components/ui/radio-group-2";
import { cn, isUserPremium, toStupidFixed } from "@/lib/utils";

export type SelectedAddon = BasketAddonItem & { id: string };

type Props = {
	service: Service;
	session: Session | null;
};

export default function PackageSection({ service, session }: Props) {
	const isInPremium = isUserPremium(session?.user.plan.duration);

	const [selectedPackage, setPackage] = useState(service.packages[0]);
	const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
	const [selectedPremiumPlan, setPremiumPlanStatus] = useState(isInPremium);

	const router = useRouter();
	const { toast } = useToast();
	const dispatch = useDispatch();

	const packageAttributes = useMemo(
		() => Object.entries(selectedPackage.attributes ?? {}),
		[selectedPackage],
	);

	const selectedAddonEntries: [string, BasketAddonItem][] = useMemo(
		() => selectedAddons.map(({ id, ...rest }) => [id, { ...rest }]),
		[selectedAddons],
	);

	const { addonTotal, packageTotal } = useMemo(
		() => calculatePackageTotal(selectedPackage, service.addons, selectedAddonEntries),
		[selectedPackage, service.addons, selectedAddonEntries],
	);

	const totalAmount = packageTotal + addonTotal;

	const onFormSubmit: FormEventHandler<HTMLFormElement> = (e) => {
		e.preventDefault();

		const { submitter } = e.nativeEvent as SubmitEvent;
		if (submitter === null) return;

		const action = addToBasket({
			serviceId: service._id,
			packageId: selectedPackage._id,
			quantity: 1,
			addons: Object.fromEntries(selectedAddonEntries),
		});
		dispatch(action);

		if (submitter.id === "add-to-basket") {
			return toast({
				title: "Item Added to cart",
				description: `${selectedPackage.title} added to cart`,
			});
		}

		if (submitter.id === "checkout") {
			if (session === null) {
				toast({ title: "You need to login first" });
				return router.push("/login");
			}

			if (selectedPremiumPlan && !isInPremium) {
				toast({ title: "Please select a premium plan" });
				return router.push("/subscription/monthly");
			}

			return router.push(`/services/frequently/${service.id}`);
		}
	};

	return (
		<form onSubmit={onFormSubmit} className="flex w-full flex-col lg:max-w-[26rem]">
			<div className="w-full lg:rounded-xl lg:bg-background lg:p-6 lg:shadow-custom">
				<h2 className="text-center text-base font-semibold">Packages</h2>
				<div className="my-4 flex justify-center gap-6 ">
					{service.packages.map((x) => (
						<PackageButton
							key={x._id}
							data={x}
							isSelected={x._id === selectedPackage._id}
							onClick={(id) => setPackage(id)}
						/>
					))}
				</div>

				<div className="space-y-2">
					<h3 className="font-semibold">{selectedPackage.title}</h3>
					<p className="whitespace-pre-wrap text-sm text-grey-foreground">
						{selectedPackage.description}
					</p>
				</div>

				<Separator className="mx-auto my-4 w-16 bg-tertiary" />

				<ul className="w-full space-y-4 text-sm text-grey-foreground">
					{packageAttributes.map(([key, value]) => (
						<li key={key} className="flex items-center justify-between">
							<span>{key}</span>
							<span>{value}</span>
						</li>
					))}
				</ul>

				<Separator className="mx-auto my-4 w-16 bg-tertiary" />

				<ul className="space-y-4 text-sm text-grey-foreground">
					{service.addons.map((addon) => (
						<li
							key={addon._id}
							className="grid grid-cols-[1fr_6.5rem] items-center justify-items-end"
						>
							<div className="flex flex-col justify-self-start">
								<span className="leading-tight">
									{addon.description}
									{/* (+{Currency} {addon.amount}) */}
								</span>
							</div>
							<PackageAddonInput
								key={addon._id}
								addon={addon}
								onChange={(value) => {
									const y: SelectedAddon = {
										id: addon._id,
										type: addon.inputType,
										value,
									};
									const copy = selectedAddons.slice(0);
									const knownIndex = copy.findIndex((x) => x.id === y.id);

									if (knownIndex !== -1) copy[knownIndex] = y;
									else copy.push(y);

									setSelectedAddons(copy);
								}}
							/>
						</li>
					))}
				</ul>

				<p className="mt-4 rounded bg-yellow-100/30 p-2 text-xsm font-medium leading-tight text-grey-foreground">
					Note: If you are unsure about the right package for you based on your
					requirements, please consult our team.
				</p>
			</div>

			<RadioGroup2
				value={selectedPremiumPlan ? "Premium" : "Basic"}
				onValueChange={(x) => setPremiumPlanStatus(x === "Premium")}
				className="my-12 space-y-2"
			>
				{!isInPremium && (
					<>
						<PackagePlanRadioCard isPremiumPlan={false} total={totalAmount} />
						<span className="mx-auto text-base text-grey-foreground">or</span>
					</>
				)}

				<PackagePlanRadioCard isPremiumPlan={true} total={totalAmount} />
			</RadioGroup2>

			<div className="flex flex-col gap-4 text-secondary-foreground">
				<Button
					id="add-to-basket"
					className="custom-box-shadow h-14 rounded-full text-sm font-semibold"
				>
					Add to Basket
				</Button>
				<Button
					id="checkout"
					className="custom-box-shadow h-14 rounded-full bg-tertiary text-sm font-semibold hover:bg-tertiary/80"
				>
					Checkout
				</Button>
			</div>
		</form>
	);
}

type PackageButtonProps = {
	data: Package;
	isSelected: boolean;
	onClick: (id: Package) => void;
};

function PackageButton({ data, isSelected, onClick }: PackageButtonProps) {
	const onButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		onClick.call(undefined, data);
	};

	return (
		<Button
			onClick={onButtonClick}
			className={clsx(
				"custom-box-shadow min-w-24",
				isSelected && "bg-tertiary hover:bg-tertiary/80",
			)}
		>
			${toStupidFixed(data.price)}
		</Button>
	);
}

type PackageAddonInputProps = {
	addon: Pick<Addon, "_id" | "inputType" | "unit" | "limit">;
	defaultValue?: SelectedAddon["value"];
	onChange: (arg0: SelectedAddon["value"]) => void;
	className?: string;
};

export function PackageAddonInput({
	addon,
	onChange,
	defaultValue,
	className,
}: PackageAddonInputProps) {
	switch (addon.inputType) {
		case AddonInputType.toggle:
			if (defaultValue !== undefined && typeof defaultValue !== "boolean") {
				throw new Error("defaultValue requires to be of type 'boolean'");
			}

			return (
				<Switch
					id={`addon-${addon._id}`}
					defaultChecked={defaultValue}
					onCheckedChange={(checked) => onChange?.call(undefined, checked)}
				/>
			);
		case AddonInputType.number:
			if (defaultValue !== undefined && typeof defaultValue !== "number") {
				throw new Error("defaultValue requires to be of type 'number'");
			}

			return (
				<PackageAddonNumberInput
					addon={addon}
					defaultValue={defaultValue}
					onChange={onChange}
					className={className}
				/>
			);
	}
}

function PackageAddonNumberInput({
	addon,
	onChange,
	defaultValue,
	className,
}: Omit<PackageAddonInputProps, "defaultValue"> & { defaultValue?: number }) {
	const [error, setError] = useState<string | undefined>();

	return (
		<div className="relative">
			<Input
				id={`addon-${addon._id}`}
				type="number"
				placeholder={addon.unit}
				min={0}
				max={addon.limit}
				defaultValue={defaultValue}
				className={cn(
					"h-8 w-24 text-sm",
					className,
					error !== undefined && "border-red-500",
				)}
				onChange={(e) => {
					const val = Number.parseInt(e.target.value);
					const overLimit = addon.limit < val;

					if (overLimit) {
						setError(`Maximum ${addon.limit}`);
						e.target.value = addon.limit.toString();
					} else if (error !== undefined) {
						setError(undefined);
					}

					onChange?.call(undefined, overLimit ? addon.limit : val);
				}}
			/>
			{error !== undefined && (
				<span className="absolute left-1 top-[2.125rem] text-xs text-red-500">{error}</span>
			)}
		</div>
	);
}

type PackageRadioCardProps = {
	total: number;
	isPremiumPlan: boolean;
};

export function PackagePlanRadioCard({ isPremiumPlan, total }: PackageRadioCardProps) {
	const id = useId();
	const planName = isPremiumPlan ? "Premium" : "Basic";
	const calculatedTotal = isPremiumPlan ? total * (1 - Discount / 100) : total;
	const discountedValue = isPremiumPlan ? total - calculatedTotal : 0;

	return (
		<label
			htmlFor={id}
			className={clsx(
				"custom-box-shadow flex h-24 cursor-pointer items-center gap-4 rounded-full px-8 lg:h-28 lg:gap-8",
				"transition-opacity duration-300 [&:has([data-state='unchecked'])]:opacity-50",
				isPremiumPlan ? "bg-tertiary/10" : "bg-grey/5",
			)}
		>
			<RadioGroupItem2 id={id} value={planName} className="absolute" />
			<div className="grid w-full grid-cols-2 items-center justify-items-center gap-x-2 gap-y-1 text-sm lg:text-sm2">
				<span className="col-span-full text-sm font-light">
					with {planName} plan you pay
				</span>

				<div className="col-span-full inline-flex items-center justify-center gap-2">
					{isPremiumPlan && (
						<div className="space-x-1">
							<span className="text-sm line-through">
								{Currency}
								{toStupidFixed(total)}
							</span>
							<span className="text-sm text-red-600 ">-{Discount}%</span>
						</div>
					)}
					<div
						className={clsx(
							"inline-flex h-6 items-center justify-center rounded bg-grey/5 px-2 font-semibold lg:h-8",
							isPremiumPlan && "font-semibold",
						)}
					>
						{Currency}
						{toStupidFixed(calculatedTotal)}
					</div>
				</div>

				<span className="col-span-full mt-1 text-xs text-grey-foreground ">
					You save{" "}
					<span className="text-tertiary">
						{Currency}
						{toStupidFixed(discountedValue)}
					</span>
				</span>
			</div>
		</label>
	);
}
